import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useCRM } from '../../../context/CRMContext';
import Header from '../../../components/layout/Header';
import AdvancedSearch from '../../../components/leads/AdvancedSearch';
import LeadTable from '../../../components/leads/LeadTable';
import useLeads from '../../../hooks/useLeads';
import { apiFetch } from '../../../utils/api';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../../components/common/Common';
import './StatusWiseLeads.css';

const ProjectLeads = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const location = useLocation();
  const { masterData } = useCRM();
  const [statusCounts, setStatusCounts] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);

  // Name comes from the dashboard click; on a refresh/deep-link fall back to
  // the same lead_projects master the lead form and filters use.
  const projectName = location.state?.projectName
    || masterData?.projects?.find(p => String(p.project_id) === String(projectId))?.name
    || 'Project';

  const baseFilters = useMemo(
    () => (selectedStatus ? { projects: projectId, status: selectedStatus } : { projects: projectId }),
    [projectId, selectedStatus]
  );
  const { leads, loading, pagination, filters, setFilters, onSearch, onPageChange, refresh, exportAll } = useLeads(baseFilters);

  // Status bifurcation for this project — same role-scoped counts the dashboard uses.
  const loadCounts = useCallback(async () => {
    try {
      const res = await apiFetch(`dashboard/project-leads/${projectId}?counts_only=1`);
      if (res.success) setStatusCounts(res.data?.statuses || []);
    } catch (err) {
      console.error('Project status counts error:', err);
    }
  }, [projectId]);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  // Keep the chips in step with the table's own focus/poll refresh.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadCounts();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [loadCounts]);

  const totalForProject = statusCounts.reduce((sum, s) => sum + Number(s.count || 0), 0);

  const handleRefresh = () => {
    refresh();
    loadCounts();
  };

  return (
    <div>
      <Header
        title={`${projectName} Leads`}
        subtitle={`${pagination.total || 0} total`}
        actions={<Button variant="outline" icon={ArrowLeft} onClick={() => navigate(-1)}>Back</Button>}
      />
      <div className="page">
        <div className="status-grid" style={{ marginTop: 28 }}>
          <div
            className={`status-card ${selectedStatus === null ? 'status-card--selected' : ''}`}
            onClick={() => setSelectedStatus(null)}
            style={{ '--status-color': '#2a5298' }}>
            <div className="status-card__top">
              <span className="status-card__dot" style={{ background: '#2a5298' }} />
              <div className="status-card__counts">
                <span className="status-card__count">{totalForProject}</span>
              </div>
            </div>
            <span className="status-card__label">All Leads</span>
          </div>
          {statusCounts.map(s => (
            <div key={s.ls_id}
              className={`status-card ${selectedStatus === s.ls_id ? 'status-card--selected' : ''}`}
              onClick={() => setSelectedStatus(selectedStatus === s.ls_id ? null : s.ls_id)}
              style={{ '--status-color': s.color || '#6b7280' }}>
              <div className="status-card__top">
                <span className="status-card__dot" style={{ background: s.color || '#6b7280' }} />
                <div className="status-card__counts">
                  <span className="status-card__count">{s.count}</span>
                </div>
              </div>
              <span className="status-card__label">{s.name}</span>
            </div>
          ))}
        </div>

        {selectedStatus && (
          <div className="status-selected-header animate-fade-in">
            <h3>Showing <strong>{statusCounts.find(s => s.ls_id === selectedStatus)?.name}</strong> leads</h3>
            <button className="btn btn--ghost btn--sm" onClick={() => setSelectedStatus(null)}>Show All</button>
          </div>
        )}

        <AdvancedSearch filters={filters} setFilters={setFilters} onSearch={onSearch} />
        <LeadTable
          leads={leads}
          loading={loading}
          pagination={pagination}
          onPageChange={onPageChange}
          onRefresh={handleRefresh}
          onExportAll={exportAll}
          showLeadType
          emptyMessage={`No leads found for ${projectName}`}
        />
      </div>
    </div>
  );
};

export default ProjectLeads;
